import type {DefaultError} from "@tanstack/react-query";
import {type UrlQueryKey, useCustomQuery} from "../../../query/queryClient.ts";
import {B} from "~/build.ts";
import {useStores} from "~/stores";
import {liveDisplay, showcaseDisplay, type PtPageDisplay} from "./paymentTermsDisplay.ts";
import type {
    ListResponse,
    PtSummary,
    PtSupplier,
    PtTermBucket,
    PtTermMapping,
    PtTrendPoint,
    SuppliersResponse
} from "~/experiments/pages/payment-terms/PaymentTermsClasses.ts";

export function paymentTermsUrl(
    action: 'trend' | 'buckets' | 'mapping' | 'suppliers' | 'summary',
    databagId?: number,
): string {
    if (B.PAYMENT_TERMS_SHOWCASE) {
        return `/payment_terms/showcase/${action}/`;
    } else {
        return `/payment_terms/${databagId ?? 0}/${action}/`;
    }
}

function usePaymentTermsQueryContext() {
    const {bagStore} = useStores();
    const databagId = bagStore.bagId || undefined;
    return {
        databagId,
        enabled: B.PAYMENT_TERMS_SHOWCASE || Boolean(databagId),
    };
}

function usePaymentTermsCollectionQuery<TResp, TOut>(
    action: 'trend' | 'buckets' | 'mapping' | 'suppliers',
    select: (data: TResp) => TOut,
) {
    const {databagId, enabled} = usePaymentTermsQueryContext();
    const url = paymentTermsUrl(action, databagId);
    return useCustomQuery<TResp, DefaultError, TOut, UrlQueryKey>({
        queryKey: [url],
        enabled,
        select,
    });
}

/** Month-by-month spend-weighted average payment term. */
export function usePaymentTermsTrendQuery() {
    return usePaymentTermsCollectionQuery<ListResponse<PtTrendPoint>, PtTrendPoint[]>(
        'trend',
        data => data.list ?? [],
    );
}

/** Spend buckets on the payment-term ladder. */
export function usePaymentTermsBucketsQuery() {
    return usePaymentTermsCollectionQuery<ListResponse<PtTermBucket>, PtTermBucket[]>(
        'buckets',
        data => data.list ?? [],
    );
}

/** Raw ERP payment-term texts normalized to net days. */
export function usePaymentTermsMappingQuery() {
    return usePaymentTermsCollectionQuery<ListResponse<PtTermMapping>, PtTermMapping[]>(
        'mapping',
        data => data.list ?? [],
    );
}

/** Supplier-level payment-term opportunities. */
export function usePaymentTermsSuppliersQuery() {
    return usePaymentTermsCollectionQuery<SuppliersResponse, PtSupplier[]>(
        'suppliers',
        data => data.suppliers ?? [],
    );
}

/** KPI / policy totals from `GET …/summary/`. */
export function usePaymentTermsSummaryQuery() {
    const {databagId, enabled} = usePaymentTermsQueryContext();
    const url = paymentTermsUrl('summary', databagId);
    return useCustomQuery<PtSummary, DefaultError, PtSummary, UrlQueryKey>({
        queryKey: [url],
        enabled,
    });
}

/** Showcase constants when the flag is on; otherwise live API values only. */
export function usePaymentTermsPageDisplay(): PtPageDisplay {
    const summaryQ = usePaymentTermsSummaryQuery();
    const suppliersQ = usePaymentTermsSuppliersQuery();
    const mappingQ = usePaymentTermsMappingQuery();
    const trendQ = usePaymentTermsTrendQuery();
    if (B.PAYMENT_TERMS_SHOWCASE) {
        return showcaseDisplay();
    }
    return liveDisplay({
        summary: summaryQ.data,
        suppliers: suppliersQ.isSuccess ? (suppliersQ.data ?? []) : undefined,
        mapping: mappingQ.isSuccess ? (mappingQ.data ?? []) : undefined,
        trend: trendQ.isSuccess ? (trendQ.data ?? []) : undefined,
    });
}
